import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault, arePricesLoading, isBNBLAvailable } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import BodyText from 'components/Luxkit/Typography/BodyText'
import GeoContext from 'contexts/geoContext'
import { isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { feesToObject, priceWithoutPropertyFees } from 'lib/offer/pricing'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import { pluralizeToString } from 'lib/string/pluralize'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import Label from 'components/Luxkit/Label'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import WalledContent from 'components/Common/WalledContent'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface Props {
  offer: App.Offer;
  rooms: Array<App.Occupants>;
  className?: string;
  cheapestSelectedOption: OfferPackageOptionView;
  onViewPackageOptions: () => void;
  calendarsLoading?: boolean;
  checkIn: moment.Moment;
  isBNBLAvailable: boolean;
  isSpoofed: boolean;
  duration: number;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
}

function FlashOfferWithFlightsSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    cheapestSelectedOption: packageView,
    rooms,
    calendarsLoading,
    checkIn,
    isSpoofed,
    duration,
    flightOrigin,
    flightDestination,
  } = props

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const dispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()

  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })

  const {
    package: pkg,
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
    pricingWithFlights,
    memberPricingWithFlights,
  } = packageView

  const isIndiaRegion = currentRegionCode === 'IN'
  const hasFlights = !!pricingWithFlights && !offer.flightCacheDisabled
  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330, [checkInMoment])

  const pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
  const memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing

  const fees = pricingToDisplay ? feesToObject(pricingToDisplay.fees) : {}

  const showMemberPrice = !!memberPricingToDisplay && memberPricingToDisplay.price > 0
  const showDiscountPill = !offer?.isDiscountPillHidden && pricingToDisplay && pricingToDisplay.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showPriceDetails = (fees.taxesAndFees ?? 0) > 0 || (fees.property ?? 0) > 0 || config.agentHub.isEnabled
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && config.businessTraveller.currentAccountMode !== 'business'
  const showFlightsDealTag = flightsEnabled && flightOrigin && flightDestination && deals.length > 0

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelPricing.price)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const isReserveForZeroTagShown = (isReserveForZeroEnabled || isSpoofed) && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInMoment)
  const isDepositTagShown = showSecureWithDepositTag

  const saleUnit = pricingToDisplay?.saleUnit ?? offer.saleUnit

  useEffect(() => {
    appDispatch(setCustomerValue(hotelPricing.price))
  }, [appDispatch, hotelPricing.price])

  const onViewHotelOptions = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    onViewPackageOptions()
  }, [dispatch, onViewPackageOptions])

  const onViewHotelFlightOptions = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: true })
    onViewPackageOptions()
  }, [dispatch, onViewPackageOptions])

  const onChangeAirport = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }, [dispatch])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !hasFlights || !flightsEnabled || !offer.bundledWithFlightsOnly,
    }),
  ], [offer, packageView, hasFlights, flightsEnabled])

  const shouldTrackPrice = !calendarsLoading && showPrices && !!hotelPricing?.price
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: hotelPricing.price.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, hotelPricing?.price, offer?.id])

  return (
    <SummaryPane border="neutral-six" borderRadius="S" padding="28 20" shadow="bottom-small" className={className} data-testid="SummaryPane">
      <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
      <WalledContent
        enforced={offer.walledGarden}
        wall={<BodyText variant="large" weight="bold">{offer.durationLabel} packages available</BodyText>}
      >
        <Group direction="vertical" gap={16} horizontalAlign="center">
          <Group direction="vertical" gap={8}>
            <Group direction="horizontal" gap={4} wrap="wrap">
              {showDiscountPill && !!pricingToDisplay?.discountPercent && <PriceRowValueDiscount
                type="estimate"
                discountPercentage={pricingToDisplay.discountPercent}
              />}
              {isDepositTagShown && !isSpoofed && <SecureWithDepositTagAndTooltip offerType={offer.type} />}
              {isTimeLimited(offer) && <OfferTimeLimitedText />}
              {isReserveForZeroTagShown && <ReserveForZeroTag />}
            </Group>
            <AccommodationPackageTilePricingBlocks>
              <Group direction="vertical" gap={12} fullHeight verticalAlign="space-between">
                <Group direction="vertical" gap={12}>
                  <BodyText variant="large" weight="bold">Hotel</BodyText>
                  {showPrices && hotelPricing &&
                    <Group direction="vertical" gap={16}>
                      <Group direction="vertical" gap={4}>
                        <PriceRowPriceCaption>{pluralizeToString('night', duration)} from</PriceRowPriceCaption>
                        <LuxPlusPriceStack
                          size="S"
                          price={hotelPricing.price}
                          memberPrice={hotelMemberPricing?.price}
                          saleUnit={saleUnit}
                          testid="pricing"
                          offerConfig={offer.luxPlus}
                          leadWithLuxPlusPrice
                        />
                      </Group>
                    </Group>
                  }
                </Group>
                <TextButton
                  kind="secondary"
                  data-testid="OfferAvailable"
                  onClick={onViewHotelOptions}
                  fit="flex"
                >
                  View options
                </TextButton>
              </Group>
              <Group direction="vertical" gap={12} fullHeight verticalAlign="space-between">
                <Group direction="vertical" gap={12}>
                  <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">Hotel + flights</BodyTextBlock>
                  {showPrices && hasFlights &&
                    <Group direction="vertical" gap={16}>
                      <Group direction="vertical" gap={4}>
                        <PriceRowPriceCaption>{pluralizeToString('night', duration)} + flights from</PriceRowPriceCaption>
                        <LuxPlusPriceStack
                          size="S"
                          price={pricingWithFlights.price}
                          memberPrice={memberPricingWithFlights?.price}
                          saleUnit="total*"
                          testid="pricing"
                          offerConfig={offer.luxPlus}
                          leadWithLuxPlusPrice
                        />
                      </Group>
                      <Group direction="vertical" gap={8}>
                        {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
                      </Group>
                    </Group>
                  }
                  {!hasFlights && flightOrigin?.code === flightDestination?.code &&
                    <PriceRowCaption>
                      No flights available from {flightOrigin?.name}. Please select another airport.
                    </PriceRowCaption>
                  }
                  {!hasFlights && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture &&
                    <PriceRowCaption>
                      {!isIndiaRegion && 'Add flights to the hotel package. Select from all major airlines at the next step.'}
                      {isIndiaRegion && 'Add flights to your hotel package from the best available airlines.'}
                    </PriceRowCaption>
                  }
                  {!hasFlights && departureTooFarInFuture &&
                    <PriceRowCaption>
                      Flights can only be booked up to 11 months in advance.
                    </PriceRowCaption>
                  }
                </Group>
                {hasFlights && (
                  <TextButton
                    kind="secondary"
                    data-testid="OfferAvailable"
                    onClick={onViewHotelFlightOptions}
                    fit="flex"
                  >
                    View options
                  </TextButton>
                )}
                {!hasFlights && flightOrigin?.code === flightDestination?.code && (
                  <TextButton kind="tertiary" fit="flex" onClick={onChangeAirport}>
                    Change airport
                  </TextButton>
                )}
              </Group>
            </AccommodationPackageTilePricingBlocks>
          </Group>
          <LoyaltyPoints calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
          <PriceRowTaxesAndFees
            endIcon={showPriceDetails && <OfferPriceDetailsRow
              offerPage
              offer={offer}
              duration={pkg.duration}
              propertyFees={fees.property}
              taxesAndFees={fees.taxesAndFees}
              hotelPrice={priceWithoutPropertyFees(hotelPricing)}
              hotelMemberPrice={hotelMemberPricing ? priceWithoutPropertyFees(hotelMemberPricing) : undefined}
              showMemberPrice={showMemberPrice}
              rooms={rooms}
              extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
              dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
              trigger="info-icon"
              cta={<TextButton kind="primary" fit="flex">View options</TextButton>}
              onCtaAfterClose={onViewPackageOptions}
            />}
          />
        </Group>
      </WalledContent>
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  rooms: state.rooms,
  offer: state.offer,
  checkIn: state.checkInDate,
  calendarsLoading: arePricesLoading(appState, state),
  isBNBLAvailable: isBNBLAvailable(state),
  isSpoofed: appState.auth.account.isSpoofed,
  duration: state.duration,
  flightOrigin: state.airport,
}), FlashOfferWithFlightsSummary)
